import { action, flow, makeObservable, observable } from 'mobx';

import { uploadFile } from 'src/api/import';
import { ErrorWithTranslation } from 'src/shared/utils/error-with-translation';
import { createPromiseController, TPromiseController } from 'src/shared/utils/promise-controller';
import { RootStore } from 'src/store';
import { I18NextStore } from 'src/store/i18next/i18next-store';
import { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import { RouterStore } from 'src/store/router/router-store';

export class ImportButtonStore {
  private readonly notifications: NotificationsStore;
  private readonly i18: I18NextStore;
  @observable isLoading: boolean = false;
  @observable isSelectPageModalOpened: boolean = false;
  @observable isImportModalOpened: boolean = false;
  @observable sheets?: string[];
  @observable selectSheetControls?: TPromiseController<string | null>;

  private router: RouterStore;

  constructor(rootStore: RootStore) {
    this.router = rootStore.router;
    this.notifications = rootStore.notifications;
    this.i18 = rootStore.i18;

    makeObservable(this);
  }

  @action.bound
  openSelectPageModal(): void {
    this.isSelectPageModalOpened = true;
  }

  @action.bound
  closeSelectPageModal(): void {
    this.isSelectPageModalOpened = false;
    this.selectSheetControls?.resolve(null);
  }

  @action.bound
  openImportModal(): void {
    this.isImportModalOpened = true;
  }

  @action.bound
  closeImportModal(): void {
    this.isImportModalOpened = false;
  }

  @action.bound
  selectSheet(sheetName: string): void {
    this.selectSheetControls?.resolve(sheetName);
  }

  @flow.bound
  async *uploadFile(file: File, directoryName: string) {
    this.isLoading = true;
    try {
      const res = await uploadFile(file);
      yield;

      const { fileId, sheets } = res;

      if (!sheets) return;

      this.sheets = sheets;
      let selectSheetRes: string | null;

      if (sheets.length > 1) {
        try {
          this.isSelectPageModalOpened = true;
          this.selectSheetControls = createPromiseController<string | null>();
          selectSheetRes = await this.selectSheetControls;

          yield;

          if (selectSheetRes === null) {
            return;
          }
        } finally {
          this.isSelectPageModalOpened = false;
          this.selectSheetControls = void 0;
        }
      } else {
        selectSheetRes = sheets[0];
      }

      this.router.push(`dicts/matching?sheetName=${selectSheetRes}&fileId=${fileId}&directoryName=${directoryName}`);
    } catch (error) {
      yield;
      console.error(error);
      if (error instanceof ErrorWithTranslation) {
        this.notifications.showNotificationMessage(error.translateError(this.i18.t));
        return;
      }
      this.notifications.showNotificationMessage(this.i18.t('directory:ImportModal.uploadError'));
    } finally {
      this.isLoading = false;
    }
  }
}
